import './SharePrincipal2.scss';
import { IContextualMenuItem, ContextualMenu } from 'office-ui-fabric-react/lib/ContextualMenu';
import { DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { Persona, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { ISharingPrincipal, SharingRole, ISharingStore, ClientId } from '../../../interfaces/SharingInterfaces';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import stringsResx from '../../Share.resx';
import Features from '@ms/odsp-utilities/lib/features/Features';
import { IIconData } from '../ShareLink/ChangePermission/ChangePermission';
import { getRoleIconNameAndTitle } from '../../../utilities/ShareHelper';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import { autobind, createRef, getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { ConfirmationDialog, ConfirmationCode } from '../ShareLink/ConfirmationDialog/ConfirmationDialog';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';

// Features
const ReviewPermissionUI = { ODB: 772 };

export interface ISharingEntityDetailProps {
  principal: ISharingPrincipal;
  takeFocus?: boolean;
  userRole?: SharingRole;
  clientId: ClientId;
  appmap?: string;
}

export interface ISharingEntityDetailState {
  showMenu: boolean;
  showConfirmationDialog: boolean;
}

export class SharePrincipal2 extends React.Component<ISharingEntityDetailProps, ISharingEntityDetailState> {
  private _store: ISharingStore;
  private _menuElement = createRef<HTMLElement>();

  static contextTypes = {
    sharingStore: PropTypes.object.isRequired
  };

  constructor(props: ISharingEntityDetailProps, context: any) {
    super(props);
    this.state = {
      showMenu: false,
      showConfirmationDialog: false
    };

    this._store = context.sharingStore;

    this._onChangeRoleClick = this._onChangeRoleClick.bind(this);
  }

  public render(): React.ReactElement<{}> {
    const props = this.props;
    const principal = props.principal;
    const isParticipantOwner = principal.role === SharingRole.owner;
    const roleText = this._computeRoleText(principal);

    return (
      <div
        className='od-ShareLink-personaItem od-SharePrincipal2'
        data-is-focusable={ true }
        aria-label={ `${principal.primaryText} ${roleText}` }
        tabIndex={ -1 }
      >
        <FocusZone direction={ FocusZoneDirection.horizontal }>
          <Persona
            className='od-SharePrincipal-persona2'
            size={ PersonaSize.size32 }
            primaryText={ principal.primaryText }
            secondaryText={ principal.isExternal ? stringsResx.externalUser : principal.secondaryText }
            showSecondaryText={ principal.isExternal || principal.secondaryText ? true : false }
            imageUrl={ principal.imageUrl }
          />
          <div className='od-SharePrincipal-permissions'>
            { isParticipantOwner ?
              <span className='od-SharePrincipal-ownerLabel' data-is-focusable={ true }>
                { stringsResx.ownerLabel }
              </span>
              : this._renderChangePermissions(principal)
            }
          </div>
        </FocusZone>
        { this.state.showConfirmationDialog && this._renderConfirmationDialog() }
      </div>
    );
  }

  private _renderChangePermissions(principal: ISharingPrincipal) {
    const role: SharingRole = principal.role;

    if (this.props.userRole === SharingRole.owner &&
      (role === SharingRole.edit || role === SharingRole.view || role === SharingRole.review)) {
      return (
        <div className='od-SharePrincipal-changePermissions'>
          { this._renderRoleIcon(principal) }
          { this._renderChangePermissionDropdown() }
        </div>
      );
    } else {
      return (
        <div className='od-SharePrincipal-permissionsRole'>
          { this._renderRoleIcon(principal) }
        </div>
      );
    }
  }

  private _renderRoleIcon(principal: ISharingPrincipal): JSX.Element {
    const icon: IIconData = getRoleIconNameAndTitle(principal.role);
    const roleText: string = this._computeRoleText(principal);
    return (
      <TooltipHost
        id={ principal.id.toString() }
        content={ roleText }
        calloutProps={ { gapSpace: 0 } }
      >
        <Icon
          className='od-SharePrincipal-roleIcon'
          iconName={ icon.name }
          aria-describedby={ 'od-SharePrincipal-tooltip' }
        />
      </TooltipHost>
    );
  }

  private _renderChangePermissionDropdown(): JSX.Element {
    let isRTL = getRTL();
    return (
      <div ref={ this._menuElement }>
        <IconButton
          className='od-SharePrincipal-changePermissionsChevron'
          onClick={ this._showMenu }
          iconProps={ { iconName: 'ChevronDown' } }
          ariaLabel={ StringHelper.format(stringsResx.permissionsLabelFor, this.props.principal.primaryText) }
        />
        { this.state.showMenu &&
          <ContextualMenu
            target={ this._menuElement.current }
            items={ this._getContextualMenuItems() }
            directionalHint={ isRTL ? DirectionalHint.bottomLeftEdge : DirectionalHint.bottomRightEdge }
            onDismiss={ this._hideMenu }
            className='ms-SharePrincipal_contextMenu'
          >
          </ContextualMenu>
        }
      </div>
    );
  }

  @autobind
  private _showMenu(): void {
    const isMenuShown = this.state.showMenu;
    this.setState({ showMenu: !isMenuShown });
  }

  @autobind
  private _hideMenu(): void {
    this.setState({ showMenu: false });
  }

  private _onChangeRoleClick(newRole: SharingRole): void {
    if (newRole !== this.props.principal.role) {
      this._store.updatePermissions(this.props.principal, newRole);
    }
  }

  @autobind
  private _stopSharingConfirmation(): void {
    this.setState({
      showConfirmationDialog: true
    });
  }

  private _renderConfirmationDialog() {
    const principal = this.props.principal;

    const participantToRemove = {
      id: principal.id,
      name: principal.primaryText,
      image: principal.imageUrl,
      isResolved: true,
      principalName: principal.loginName,
      userId: principal.loginName,
      email: principal.email,
      job: principal.secondaryText,
      isExternal: principal.isExternal,
      loginName: principal.loginName,
      principalType: principal.type,
    };

    return (
      <ConfirmationDialog
        confirmationCode={ ConfirmationCode.participant }
        participant={ participantToRemove }
        onApply={ this._onStopSharingClick.bind(this) }
        onCancel={ this._onCancel }
      />
    );
  }

  @autobind
  private _onCancel(): void {
    this.setState({
      showConfirmationDialog: false
    });
  }

  private _onStopSharingClick(ev: React.MouseEvent<{}>): void {
    this._store.updatePermissions(this.props.principal, SharingRole.none);
  }

  private _getContextualMenuItems(): IContextualMenuItem[] {
    const props = this.props;
    const role = props.principal.role;
    const showReview = Features.isFeatureEnabled(ReviewPermissionUI);
    const doesClientSupportReview = (props.clientId === ClientId.word) || (props.appmap && props.appmap === 'ms-word');

    let role1: SharingRole;
    let string1: string;
    let role2: SharingRole;
    let string2: string;
    let currentRoleString: string;
    switch (role) {
      case SharingRole.view:
        currentRoleString = stringsResx.canViewLabel;
        role1 = SharingRole.edit;
        string1 = stringsResx.canEditLabel;
        role2 = SharingRole.review;
        string2 = stringsResx.canReviewRole;
        break;
      case SharingRole.review:
        currentRoleString = stringsResx.canReviewRole;
        role1 = SharingRole.edit;
        string1 = stringsResx.canEditLabel;
        role2 = SharingRole.view;
        string2 = stringsResx.canViewLabel;
        break;
      case SharingRole.edit:
        currentRoleString = stringsResx.canEditLabel;
        role1 = SharingRole.view;
        string1 = stringsResx.canViewLabel;
        role2 = SharingRole.review;
        string2 = stringsResx.canReviewRole;
        break;
    }

    const contextualMenuItems: IContextualMenuItem[] = showReview && doesClientSupportReview ?
      [{
        key: 'editable1',
        name: currentRoleString,
        onClick: this._onChangeRoleClick.bind(this, role),
        checked: true,
        iconProps: {
          iconName: 'CheckMark'
        }
      },
      {
        key: 'editable2',
        name: string1,
        onClick: this._onChangeRoleClick.bind(this, role1)
      },
      {

        key: 'editable3',
        name: string2,
        onClick: this._onChangeRoleClick.bind(this, role2)
      },
      {

        key: 'stopSharing',
        name: `${stringsResx.stopSharing}`,
        onClick: this._stopSharingConfirmation
      }]
      :
      [{
        key: 'editable1',
        name: currentRoleString,
        onClick: this._onChangeRoleClick.bind(this, role),
        checked: true,
        iconProps: {
          iconName: 'CheckMark'
        }
      },
      {
        key: 'editable2',
        name: string1,
        onClick: this._onChangeRoleClick.bind(this, role1)
      },
      {
        key: 'stopSharing',
        name: `${stringsResx.stopSharing}`,
        onClick: this._stopSharingConfirmation
      }];

    return contextualMenuItems;
  }

  private _computeRoleText(principal: ISharingPrincipal): string {
    const strings = stringsResx;
    const role = principal.role;

    if (typeof role === 'number') { // SharingRole.owner === 0, so need to check type instead of truthiness.
      switch (role) {
        case SharingRole.edit:
          return strings.canEditLabel;
        case SharingRole.review:
          return stringsResx.canReviewRole;
        case SharingRole.view:
          return strings.canViewLabel;
        case SharingRole.owner:
          return strings.ownerLabel;
        default:
          return '';
      }
    } else if (principal.sharingLinkKind) {
      return strings.accessViaSharingLink;
    } else {
      return '';
    }
  }
}
